import moment from "moment";
import { SettingsService } from "@/services/settingsApi";
import store from "@/store";

const initialState = () => ({selectedLabel: null});

export default {
	namespaced: true,

	state: initialState(),

	mutations: {
		resetPriceHistory,
		setSelectedLabel,
		setPriceHistory,
		setCurrentPrice
	},

	actions: {
		addNewPrice,
		updateCurrentPrice,
		updatePriceHistory,
		addNewPeriod
	},

	getters: {
		// priceHistory is sotred, most recent priceHisotory will be first in list
		priceHistory: state => {
			if (!state.selectedLabel)
				return [];


			return state.selectedLabel.price_history
				// sort them by end date 'desc'
				.sort((dateA, dateB) => {
					return moment(dateB.end_date) - moment(dateA.end_date);
				})
				// sort them by start date 'desc'
				.sort((dateA, dateB) => {
					return moment(dateB.start_date) - moment(dateA.start_date);
				});
		},

		currentPrice: (state, getters) => {

			/*
			 * if we don't have price history
			 * the start date of the price will be outlet_start date
			 */
			let startDate = store.getters["user/activeOutlet"].outlet_start_date;

			if (getters.priceHistory.length) {

				/*
				 * if we have price_history
				 * the start_date will be the the end_date of the most recent price history
				 */
				startDate = getters.priceHistory[0].end_date;
			}

			return {
				price: state.selectedLabel?.price,
				start_date: startDate,
				end_date: null
			};
		}

	}
};

function resetPriceHistory(state) {
	Object.assign(state, initialState());
}

function setSelectedLabel(state, label) {
	state.selectedLabel = label;
}

function setPriceHistory(state, priceHistory) {
	state.selectedLabel.price_history = priceHistory;
}

function setCurrentPrice(state, newPrice) {
	state.selectedLabel.price = newPrice;
	state.selectedLabel.edited_at = moment().format("YYYY-MM-DD");
}

async function addNewPrice({
	state, getters, commit
}, newPriceInfo) {

	const shouldOverrideCurrentPrice = moment(getters.currentPrice.start_date).isSame(newPriceInfo.start_date, "day"),
		lastCurrentPriceInfo = getters.currentPrice;


	if (shouldOverrideCurrentPrice) {

		/*
		 * if the newPrice info has the same start date as the last price info,
		 * in that case just override the last priceInfo.current_price with the new price
		 */
		const requestBody = {
			label_id: state.selectedLabel.label_id,
			current_price: newPriceInfo.price,
			price_history: getters.priceHistory
		};

		await SettingsService.setLabelPrice(requestBody);
		commit("setCurrentPrice", newPriceInfo.price);
		return;
	}

	/*
	 * if the newPrice info has different start date from the last price info,
	 * move the last priceInfo in the price history list
	 * and put the new price history to be the current price
	 */
	lastCurrentPriceInfo.end_date = moment(newPriceInfo.start_date).format("YYYY-MM-DD");

	// put the last current price in price history
	// eslint-disable-next-line
	const newPriceHistory = [ lastCurrentPriceInfo, ...getters.priceHistory ],
		requestBody = {
			label_id: state.selectedLabel.label_id,
			current_price: newPriceInfo.price,
			price_history: newPriceHistory
		};

	await SettingsService.setLabelPrice(requestBody);

	commit("setCurrentPrice", newPriceInfo.price);
	commit("setPriceHistory", newPriceHistory);
}

async function updateCurrentPrice({
	state, getters, commit
}, newPrice) {
	const requestBody = {
		label_id: state.selectedLabel.label_id,
		current_price: newPrice,
		price_history: getters.priceHistory
	};

	await SettingsService.setLabelPrice(requestBody);

	commit("setCurrentPrice", newPrice);
}

async function updatePriceHistory({
	state, getters, commit
}, editedPriceInfo) {
	let priceHistory = getters.priceHistory.filter(priceInfo => {
		return !moment(priceInfo.start_date).isSame(editedPriceInfo.start_date, "day");
	});

	priceHistory = [ editedPriceInfo, ...priceHistory ];

	const requestBody = {
		label_id: state.selectedLabel.label_id,
		current_price: state.selectedLabel.price,
		price_history: priceHistory
	};

	await SettingsService.setLabelPrice(requestBody);

	commit("setPriceHistory", priceHistory);
}

async function addNewPeriod({
	state, getters, commit
}, newPeriod) {
	let resolvedStartDatePriceInfo = null,
		resolvedEndDatePriceInfo = null,
		newPriceHistory = [];

	newPeriod.start_date = moment(newPeriod.start_date).format("YYYY-MM-DD");
	newPeriod.end_date = moment(newPeriod.end_date).format("YYYY-MM-DD");

	const conflictStartDatePriceInfo = getters.priceHistory.find(priceInfo => {
			return (
				moment(newPeriod.start_date).isAfter(priceInfo.start_date, "day") &&
				moment(newPeriod.start_date).isBefore(priceInfo.end_date, "day")
			);
		}),

		conflictEndDatePriceInfo = getters.priceHistory.find(priceInfo => {
			return (
				moment(newPeriod.end_date).isAfter(priceInfo.start_date, "day") &&
				moment(newPeriod.end_date).isBefore(priceInfo.end_date, "day")
			);
		}),
		removedOverlappedPriceInfo = getters.priceHistory
			.filter(priceInfo => {
				return !(
					moment(priceInfo.start_date).isSameOrAfter(newPeriod.start_date, "day") &&
					moment(priceInfo.end_date).isSameOrBefore(newPeriod.end_date, "day")
				);
			})
			// also remove conflicting PriceInfo(s), we will add resolved PriceInfo object(s) later
			.filter(priceInfo => ![ conflictStartDatePriceInfo, conflictEndDatePriceInfo ].includes(
				priceInfo
			));

	newPriceHistory = [ newPeriod, ...removedOverlappedPriceInfo ];

	if (conflictStartDatePriceInfo) {
		resolvedStartDatePriceInfo = {
			...conflictStartDatePriceInfo,
			end_date: newPeriod.start_date
		};

		//  add resolved PriceInfo object
		newPriceHistory.push(resolvedStartDatePriceInfo);
	}

	if (conflictEndDatePriceInfo) {
		resolvedEndDatePriceInfo = {
			...conflictEndDatePriceInfo,
			start_date: newPeriod.end_date
		};

		//  add resolved PriceInfo object
		newPriceHistory.push(resolvedEndDatePriceInfo);
	}

	// eslint-disable-next-line one-var
	const requestBody = {
		label_id: state.selectedLabel.label_id,
		current_price: state.selectedLabel.price,
		price_history: newPriceHistory
	};

	await SettingsService.setLabelPrice(requestBody);
	commit("setPriceHistory", newPriceHistory);
}

